import { actions } from "../../../Redux/store";
import {
  IonContent,
  IonModal,
  IonSpinner,
  IonSkeletonText,
} from "@ionic/react";
import { useSelector } from "react-redux";
import closeIcon from "../../../assets/imgs/close.svg";
import shareIos from "../../../assets/imgs/share-ios.svg";
import iconNews from "../../../assets/imgs/icon-new-post-ai.svg";
import timeIcon from "../../../assets/imgs/time_icon.svg";
import rightArrow from "../../../assets/imgs/right_arrow.svg";
import dislike from "../../../assets/imgs/dislike_active.svg";
import like from "../../../assets/imgs/like.svg";
import likeInactive from "../../../assets/imgs/like-inactive.svg";
import dislikeInactive from "../../../assets/imgs/dislike.svg";
import { Colors } from "../../Commons/Colors/Colors";
import { useState, useEffect, useRef, useCallback } from "react";
import { shareContent } from "../../../Services/shareContent";
import { shortTimeAgo } from "../../../utils/dateUtils";
import Markdown from "markdown-to-jsx";
import { hapticsImpactLight, markdownStyleOverrides } from "../../../lib/utils";
import searchApplied from "../../../assets/imgs/search_applied.svg";
import { createContextualQuery } from "../../../utils/queryUtils";
import { Capacitor } from "@capacitor/core";
import { useMixpanel } from "../../../Context/MixpanelContext/MixpanelProvider";
import { getArticleById, articleReact } from "../../../Controllers/ArticlesApi";
import { dotWave } from "ldrs";
import { useArticleClickStore } from "../../../zustandStore/articleClickStore";
import { requestReview } from "../../../Services/inAppReview";
import NewsDetailsSkeleton from "./NewsDetailsSkeleton";
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";

dotWave.register();

export default function NewsDetailsModal() {
  const isOpenNewsDetails = useSelector(
    (state: any) => state.modal.newsDetilsModal?.open,
  );
  const articleId = useSelector(
    (state: any) => state.modal.newsDetilsModal?.data?.article_id,
  );
  const { articleClicks, clearArticleClicks } = useArticleClickStore();

  const [articleData, setArticleData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [likeClicked, setLikeClicked] = useState(false);
  const [dislikeClicked, setDislikeClicked] = useState(false);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [dislikeAnimating, setDislikeAnimating] = useState(false);

  // Scroll-related refs and state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const handleLikeClick = async () => {
    // If already liked, don't do anything
    if (likeClicked) return;

    hapticsImpactLight();
    setLikeClicked(true);
    setDislikeClicked(false);

    // Trigger animation
    setLikeAnimating(true);
    setTimeout(() => setLikeAnimating(false), 300);

    if (articleId) {
      try {
        await articleReact(articleId, { reaction_type: "like" });
      } catch (error) {
        console.error("Error liking article:", error);
      }
    }
  };

  const handleDislikeClick = async () => {
    // If already disliked, don't do anything
    if (dislikeClicked) return;

    hapticsImpactLight();
    setDislikeClicked(true);
    setLikeClicked(false);

    // Trigger animation
    setDislikeAnimating(true);
    setTimeout(() => setDislikeAnimating(false), 300);

    if (articleId) {
      try {
        await articleReact(articleId, { reaction_type: "dislike" });
      } catch (error) {
        console.error("Error disliking article:", error);
      }
    }
  };

  // Fetch article data when articleId changes
  useEffect(() => {
    const fetchArticleData = async () => {
      if (articleId && isOpenNewsDetails) {
        setIsLoading(true);
        try {
          const fetchedArticle = await getArticleById(articleId);
          console.log("fetchedArticle", fetchedArticle);
          setArticleData(fetchedArticle?.data?.article);

          // Set like/dislike state based on reaction data from API
          const reactionType = fetchedArticle?.data?.reaction?.reaction_type;
          if (reactionType === "like") {
            setLikeClicked(true);
            setDislikeClicked(false);
          } else if (reactionType === "dislike") {
            setDislikeClicked(true);
            setLikeClicked(false);
          } else {
            // Reset if no reaction or unknown reaction type
            setLikeClicked(false);
            setDislikeClicked(false);
          }

          console.log(articleClicks, "articleClicks*");
          // Check articleClicks length after API call
          if (articleClicks.length > 5) {
            clearArticleClicks();
            requestReview();
          }
        } catch (error) {
          console.error("Error fetching article details:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchArticleData();
  }, [articleId, isOpenNewsDetails, articleClicks]);

  const toggleModals = () => {
    actions.modal.closeNewsDetailsModal(null);
  };

  const [followUpInput, setFollowUpInput] = useState("");
  const [isFollowUpFocused, setIsFollowUpFocused] = useState(false);
  const followUpRef = useRef<HTMLTextAreaElement>(null);
  const platform = Capacitor.getPlatform();
  const Mixpanel = useMixpanel();
  const cleanText = (text: string) => {
    if (!text) return '';
    return text
      .replace(/\s+/g, ' ')
  };

  const clickSuggestedQuestions = (suggestion: string) => {
    Mixpanel.trackEvent("select_premade_suggestion", {
      name: "Selected the Premade prompt in News Feed",
      chatbotQuery: suggestion,
    });
    hapticsImpactLight();
    const contextualQuery = createContextualQuery(
      articleData?.title,
      cleanText(articleData?.full_text || ''),
      suggestion,
    );
    actions.modal.openChatbotSearchModal({
      suggestion: contextualQuery,
      showingSuggestion: suggestion,
    });
  };

  const handleShare = async (title: string, text: string) => {
    try {
      hapticsImpactLight();
      await shareContent({
        title: title,
        url: `https://rotobot.ai/article/${articleId}`,
      });
    } catch (error) {
      // Handle any specific UI error handling here if needed
      console.error("Error in handleShare:", error);
    }
  };
  // -----------------------------------------------

  // Scroll handling functions
  const handleScroll = useCallback(() => {
    setIsScrolling(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Add functions for textarea handling
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    if (!element) return;

    // Reset height to auto to get the correct scrollHeight
    element.style.height = "auto";

    // Get the scroll height (content height)
    const scrollHeight = element.scrollHeight;

    // Calculate new height (clamped between min and max)
    const minHeight = 52; // Minimum height (matches the default height)
    const maxHeight = 120; // Maximum height before scrolling begins

    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    // Apply the new height
    element.style.height = `${newHeight}px`;
  };

  // Base style for follow-up input
  const baseStyleFollowUp: React.CSSProperties = {
    width: "100%",
    minHeight: isFollowUpFocused ? "52px" : "52px",
    maxHeight: isFollowUpFocused ? "120px" : "52px",
    height: "52px", // Initial height always 52px
    padding: isFollowUpFocused ? "16px 0" : "16px",
    paddingLeft: isFollowUpFocused ? "16px" : "24px",
    paddingRight: isFollowUpFocused ? "16px" : undefined,
    backgroundColor: "#1A1E2B",
    borderTopLeftRadius: isFollowUpFocused ? "0px" : "50px",
    borderBottomLeftRadius: isFollowUpFocused ? "0px" : "50px",
    borderTopRightRadius: followUpInput
      ? "0px"
      : isFollowUpFocused
        ? "0px"
        : "50px",
    borderBottomRightRadius: followUpInput
      ? "0px"
      : isFollowUpFocused
        ? "0px"
        : "50px",
    color: "white",
    fontSize: "14px",
    overflowY: isFollowUpFocused ? "auto" : "hidden",
  };

  const handleFollowUpChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFollowUpInput(e.target.value);

    // Only adjust height if focused
    if (isFollowUpFocused) {
      adjustTextareaHeight(e.target);
    } else {
      // Ensure height is fixed at 52px when not focused
      e.target.style.height = "52px";
    }
  };

  // Add effect to ensure the textarea height is correct based on focus state
  useEffect(() => {
    if (followUpRef.current) {
      if (isFollowUpFocused) {
        adjustTextareaHeight(followUpRef.current);
      } else {
        followUpRef.current.style.height = "52px";
      }
    }
  }, [isFollowUpFocused]);

  const handleFollowUpFocus = () => {
    setIsFollowUpFocused(true);
    // Re-enable tab navigation when user intentionally focuses
    if (followUpRef.current) {
      followUpRef.current.tabIndex = 0;
    }
  };

  const handleFollowUpBlur = () => {
    setIsFollowUpFocused(false);
    setFollowUpInput("");
    // Reset the height of the textarea to its original height (52px)
    if (followUpRef.current) {
      followUpRef.current.style.height = "52px";
      // Disable tab navigation when not focused to prevent autofocus
      followUpRef.current.tabIndex = -1;
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  // Add effect to handle clicks outside the input area
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isFollowUpFocused &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        followUpRef.current !== event.target
      ) {
        handleFollowUpBlur();
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFollowUpFocused]);

  const handleFollowUpSubmit = () => {
    if (followUpInput.trim() === "") return;

    Mixpanel.trackEvent("enter_custom_prompt", {
      name: "Entered custom prompt in News Feed",
      chatbotQuery: followUpInput,
    });

    const contextualQuery = createContextualQuery(
      articleData?.title,
      cleanText(articleData?.full_text || ''),
      followUpInput,
    );

    actions.modal.openChatbotSearchModal({
      suggestion: contextualQuery,
      showingSuggestion: followUpInput,
    });
    setFollowUpInput("");
    setIsFollowUpFocused(false);

    // Reset textarea height and prevent future autofocus
    if (followUpRef.current) {
      followUpRef.current.style.height = "52px";
      followUpRef.current.blur();
      followUpRef.current.tabIndex = -1;
    }

    hapticsImpactLight();
  };

  const canDismissModal = async (data?: any, role?: string) => {
    return role === "gesture" ? false : true;
  };

  const modal = useRef<HTMLIonModalElement>(null);

  function dismiss() {
    modal.current?.dismiss();
    toggleModals();
  }

  const closeNewsDetailsModal = () => {
    modal.current?.dismiss();
    actions.modal.closeNewsDetailsModal(null);
    hapticsImpactLight();
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent default enter behavior (new line)
      if (followUpInput.trim()) {
        handleFollowUpSubmit();
      }
    }
  };

  return (
    <IonModal
      canDismiss={canDismissModal}
      onDidDismiss={dismiss}
      isOpen={isOpenNewsDetails}
      backdropDismiss={false}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      className={`${platform === "web" ? "full-modal" : "custom-modal"}`}
      ref={modal}
      handle={false}
    >
      <div className="news-modal-container relative w-full h-full overflow-hidden">
        {/* Static Header */}
        <div
          className="absolute w-full z-10 p-3"
          style={{ pointerEvents: isScrolling ? "none" : "auto" }}
        >
          <div className="flex justify-between">
            {!isLoading && (
              <>
                <motion.div
                  onClick={closeNewsDetailsModal}
                  className="bg-black/15 backdrop-blur-md w-[40px] h-[40px] flex items-center justify-center rounded-full cursor-pointer outline-none"
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  animate={{ opacity: isScrolling ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <img
                    src={closeIcon}
                    alt="closeIcon"
                    className="w-[15px] h-[15px]"
                  />
                </motion.div>
                <motion.div
                  onClick={() =>
                    handleShare(articleData?.title, cleanText(articleData?.full_text || ''))
                  }
                  className="bg-black/15 backdrop-blur-md w-[40px] h-[40px] flex items-center justify-center rounded-full mr-0 [@media(min-width:576px)]:mr-[20px] cursor-pointer outline-none"
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  animate={{ opacity: isScrolling ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <img src={shareIos} alt="shareIos" />
                </motion.div>
              </>
            )}
          </div>
        </div>

        {/* Main Content with Custom Scroll */}
        <div
          ref={scrollContainerRef}
          className="news-modal-scroll-container h-full overflow-y-auto overflow-x-hidden overscroll-none"
          onScroll={handleScroll}
          style={{
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {isLoading ? (
            <NewsDetailsSkeleton />
          ) : articleData ? (
            <div ref={contentRef} className="min-h-full">
              <div className="flex-col items-center w-full">
                <div className="relative w-full max-w-[576px] mx-auto">
                  <img
                    src={articleData?.header_image_url || ""}
                    alt={"newsDetails"}
                    className="news-modal-image w-full h-full object-cover"
                  />
                </div>

                <div className="p-4 w-full">
                  <span className="text-white text-[20px] font-bold">
                    {articleData?.title || ""}
                  </span>
                  <div className="flex justify-between items-center w-full mt-4 border-b border-white/10 pb-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <img src={timeIcon} alt="timeIcon" />
                      </div>
                      <div>
                        <span className="text-white font-normal text-[12px]">
                        Published {shortTimeAgo(articleData?.date_created)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <article className="prose prose-slate max-w-none w-full article-details">
                      <Markdown
                        options={{
                          overrides: markdownStyleOverrides,
                        }}
                      >
                        {cleanText(articleData?.full_text || '')}
                      </Markdown>
                    </article>
                  </div>

                  <div className="mt-12 flex items-center  gap-[42px]">
                    <div>
                      <span className="text-white text-[16px] font-semibold">
                        Did you like this content?
                      </span>
                    </div>

                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-[31.2px] h-[31.2px] cursor-pointer rounded-[6px] bg-white/10 flex items-center justify-center transition-all duration-300 ${likeClicked ? "bg-white/20" : ""}`}
                        onClick={handleLikeClick}
                      >
                        <img
                          src={likeClicked ? like : likeInactive}
                          alt="like"
                          className={`transition-all duration-300 ${
                            likeAnimating
                              ? "scale-90 rotate-[10deg]"
                              : "hover:scale-105 hover:rotate-[-5deg]"
                          } `}
                        />
                      </div>
                      <div
                        className={`w-[31.2px] h-[31.2px] cursor-pointer rounded-[6px] bg-white/10 flex items-center justify-center transition-all duration-300 ${dislikeClicked ? "bg-white/20" : ""}`}
                        onClick={handleDislikeClick}
                      >
                        <img
                          src={dislikeClicked ? dislike : dislikeInactive}
                          alt="dislike"
                          className={`transition-all duration-300 ${
                            dislikeAnimating
                              ? "scale-90 rotate-[-10deg]"
                              : "hover:scale-105 hover:rotate-[5deg]"
                          } `}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Suggested Questions */}
                  <div className="mt-12 w-full ">
                    <div className="flex items-center gap-2 w-full">
                      <img
                        src={iconNews}
                        className="w-[28px] h-[28px]"
                        alt="iconNews"
                      />
                      <span className="text-white text-[16px] font-semibold">
                        Suggested Questions
                      </span>
                    </div>

                    <div className="flex-col mt-2 w-full pb-[80px]">
                      {articleData?.suggested_questions?.map(
                        (question: any, index: number) => (
                          <div
                            onClick={() => clickSuggestedQuestions(question)}
                            key={index}
                            className=" flex justify-between gap-3 items-center border-b border-white/10 py-4 pr-1"
                          >
                            <span className="text-white text-[12px] font-medium">
                              {question}
                            </span>
                            <img src={rightArrow} alt="rightArrow" />
                          </div>
                        ),
                      )}
                    </div>

                    {/* Follow-Up Input */}
                    <div
                      ref={containerRef}
                      className={`flex fixed ${isFollowUpFocused ? "w-full left-0" : "w-[94%] left-[3%]"} z-50 ${isFollowUpFocused ? "bottom-[0px]" : "bottom-[25px]"} 
                      transition-all duration-500 ease-in-out transform origin-center`}
                      style={{
                        transitionProperty:
                          "width, left, bottom, transform, opacity",
                        willChange: "width, left, bottom, transform, opacity",
                      }}
                    >
                      <textarea
                        ref={followUpRef}
                        className="html-textarea transition-all duration-300"
                        placeholder="Ask a Question...."
                        value={followUpInput}
                        onChange={handleFollowUpChange}
                        onFocus={handleFollowUpFocus}
                        onBlur={(e) => {
                          // Only call handleFollowUpBlur if the click is outside
                          // the container (handled by the useEffect above)
                          if (
                            !containerRef.current?.contains(
                              e.relatedTarget as Node,
                            ) &&
                            e.relatedTarget !==
                              document.getElementById("button-chat")
                          ) {
                            e.preventDefault(); // Prevent default blur behavior
                          }
                        }}
                        onKeyDown={handleEnterKeyPress}
                        tabIndex={-1}
                        data-no-autofocus="true"
                        style={{
                          ...baseStyleFollowUp,
                          resize: "none",
                          transition: "all 0.3s ease-in-out",
                          transformOrigin: "center",
                        }}
                      />

                      {followUpInput && (
                        <div
                          id="button-chat"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={
                            followUpInput.trim()
                              ? handleFollowUpSubmit
                              : undefined
                          }
                          className={`flex pr-4 items-center transition-all duration-300`}
                          style={{
                            backgroundColor: "#1A1E2B",
                            borderTopRightRadius: isFollowUpFocused
                              ? "0px"
                              : "50px",
                            borderBottomRightRadius: isFollowUpFocused
                              ? "0px"
                              : "50px",
                            height: followUpRef.current
                              ? followUpRef.current.style.height
                              : "52px",
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.3s ease-in-out",
                          }}
                        >
                          <div
                            className="cursor-pointer"
                            style={{
                              animation: followUpInput.trim()
                                ? "bounceInRight 0.5s ease-out forwards"
                                : "none",
                              opacity: followUpInput.trim() ? 1 : 0,
                              transform: followUpInput.trim()
                                ? "scale(1)"
                                : "scale(0)",
                              transition:
                                "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
                            }}
                          >
                            <img
                              src={searchApplied}
                              alt="searchApplied"
                              className="transition-all duration-300 hover:scale-110 active:scale-95"
                              style={{
                                filter:
                                  "drop-shadow(0 0 2px rgba(255,255,255,0.3))",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-white">No article data available</p>
            </div>
          )}
        </div>
      </div>
    </IonModal>
  );
}
